// Global axios interceptor for the customer + mechanic SPA.
//
// Two coexisting auth shapes:
//   1. Mechanic flow — `authToken` (HS256 JWT issued by /api/mechanic_login).
//      Sent on every ridenrepair.com call after the mechanic logs in.
//   2. Customer flow — `customer_jwt` (HS256 JWT issued by /api/site_otp_login).
//      Sent on every ridenrepair.com call after the customer verifies OTP.
//
// If neither is present (anonymous browsing of the site), no Authorization
// header is attached — backend routes that allow anon (e.g. /api/register,
// /api/mechanic_login itself) continue to work.
//
// Also patches global fetch() so non-axios callers (raw fetch in some
// components) get the same header.

import axios from 'axios';
import { getLocalItem } from './storage';

const RIDENREPAIR_HOST_RE = /^https?:\/\/(?:www\.)?ridenrepair\.com\b/i;

function pickToken() {
  // Mechanic flow takes precedence on mechanic-* pages; otherwise customer.
  // Pre-login both are null and we send nothing.
  try {
    return getLocalItem('authToken') || getLocalItem('customer_jwt') || null;
  } catch (e) {
    return null;
  }
}

// NOTE: do NOT set axios.defaults.headers.common.Authorization here.
// axios 0.27 merges defaults + per-request headers in a way that, when a
// caller ALSO passes `headers: { Authorization: ... }` explicitly (as
// mechanic-login.jsx and mechanic-booking-details.jsx do), the browser
// receives a duplicated header: `Bearer X, Bearer X`. expressJwt then
// fails to parse the comma-joined value and returns 401.
// The request interceptor below is sufficient — it adds the header only
// when no explicit Authorization is present.

function shouldAttach(url) {
  if (!url) return false;
  // Relative URL → same-origin → ridenrepair.com in prod, localhost in dev
  if (url.startsWith('/')) return true;
  return RIDENREPAIR_HOST_RE.test(url);
}

// Diagnostic ring buffer — survives `drop_console: true` (which strips
// console.log). Read in browser DevTools: `window.__authDebug` or
// `copy(JSON.stringify(window.__authDebug,null,2))`.
if (typeof window !== 'undefined') {
  window.__authDebug = window.__authDebug || [];
}
function _dbg(entry) {
  try {
    if (typeof window === 'undefined') return;
    window.__authDebug.push({ t: Date.now(), ...entry });
    if (window.__authDebug.length > 200) window.__authDebug.shift();
    // Also console.warn (NOT stripped by drop_console) so it shows in console.
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[AUTH_INT]', entry);
    }
  } catch (e) { /* noop */ }
}

// ─── axios global ──────────────────────────────────────────────────────
// STRIP-THEN-SET strategy: regardless of what the caller passed (explicit
// header, axios default, common header), we strip every Authorization
// variant from config.headers AND from the per-method sub-objects (axios
// 0.27 merges config.headers.common / .get / .post / etc into the final
// request), then we set exactly ONE Authorization header. This guarantees
// the browser sends exactly one header value — no `Bearer X, Bearer X`.
function _extractExplicitToken(headers) {
  if (!headers) return null;
  const v = headers.Authorization || headers.authorization;
  if (!v) return null;
  const s = String(v).trim();
  if (s.toLowerCase().startsWith('bearer ')) return s.slice(7).trim();
  return s;
}

function _stripAuthFromHeaderBag(bag) {
  if (!bag || typeof bag !== 'object') return;
  // Delete every case variant we've ever seen in axios + the wild.
  delete bag.Authorization;
  delete bag.authorization;
  delete bag.AUTHORIZATION;
}

axios.interceptors.request.use((config) => {
  const url = config.url || '';
  const fullUrl = config.baseURL ? config.baseURL.replace(/\/$/, '') + url : url;
  const attach = shouldAttach(fullUrl);
  const tok = pickToken();
  config.headers = config.headers || {};
  const explicitTok = _extractExplicitToken(config.headers);
  const hadExplicit = !!explicitTok;
  if (!attach) {
    _dbg({ src: 'axios', url: fullUrl, attach, hasTok: !!tok, tokLen: tok ? String(tok).length : 0, explicit: hadExplicit, finalLen: 0 });
    return config;
  }
  // Effective token: prefer caller-supplied; otherwise localStorage.
  const effective = explicitTok || tok;
  // STRIP every Authorization variant from every header bag axios may merge.
  _stripAuthFromHeaderBag(config.headers);
  _stripAuthFromHeaderBag(config.headers.common);
  if (config.method) _stripAuthFromHeaderBag(config.headers[config.method.toLowerCase()]);
  _stripAuthFromHeaderBag(config.headers.get);
  _stripAuthFromHeaderBag(config.headers.post);
  _stripAuthFromHeaderBag(config.headers.put);
  _stripAuthFromHeaderBag(config.headers.patch);
  _stripAuthFromHeaderBag(config.headers.delete);
  _stripAuthFromHeaderBag(config.headers.head);
  _stripAuthFromHeaderBag(config.headers.options);
  // SET exactly one.
  if (effective) {
    config.headers.Authorization = `Bearer ${effective}`;
  }
  const finalVal = config.headers.Authorization || '';
  _dbg({ src: 'axios', url: fullUrl, attach, hasTok: !!tok, tokLen: tok ? String(tok).length : 0, explicit: hadExplicit, finalLen: finalVal.length });
  return config;
});

// ─── fetch() shim — browser only (SSR has no window/fetch/Headers) ─────
// Same STRIP-THEN-SET strategy as the axios interceptor: extract caller's
// explicit Authorization (if any), delete every variant, then set exactly
// one. Headers in fetch land are case-insensitive via the Headers class,
// but we also handle plain-object init.headers explicitly because some
// callers pass a literal object — and we want to be defensive.
if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const _origFetchRaw = window.fetch;
  const _origFetch = _origFetchRaw.bind(window);
  const _wrappedFetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (shouldAttach(url)) {
        init = init || {};
        const rawHeaders = init.headers || (typeof input !== 'string' && input.headers) || {};
        const headers = new Headers(rawHeaders);
        // Extract any caller-supplied token (Headers normalizes case).
        let explicitTok = null;
        const existing = headers.get('Authorization');
        if (existing) {
          const s = String(existing).trim();
          explicitTok = s.toLowerCase().startsWith('bearer ') ? s.slice(7).trim() : s;
        }
        const tok = pickToken();
        const effective = explicitTok || tok;
        // STRIP — guarantees no duplicate even if caller passed a plain
        // object with mixed-case keys that Headers de-dup'd into one slot.
        headers.delete('Authorization');
        // SET exactly one.
        if (effective) {
          headers.set('Authorization', `Bearer ${effective}`);
        }
        init.headers = headers;
        const finalVal = headers.get('Authorization') || '';
        _dbg({ src: 'fetch', url, attach: true, hasTok: !!tok, tokLen: tok ? String(tok).length : 0, explicit: !!explicitTok, finalLen: finalVal.length });
      }
    } catch (e) {
      // best-effort — never break fetch on a header problem
    }
    return _origFetch(input, init);
  };
  // Preserve the original fetch's prototype + own properties so that any
  // later wrapper (e.g. Microsoft Clarity, Sentry, NewRelic) that reads
  // sub-properties off window.fetch (`.prototype`, `.bind`, custom symbols)
  // sees the same shape and doesn't blow up with
  // "Cannot read properties of undefined (reading 'apply')".
  try {
    Object.setPrototypeOf(_wrappedFetch, Object.getPrototypeOf(_origFetchRaw));
    Object.getOwnPropertyNames(_origFetchRaw).forEach((k) => {
      if (k === 'length' || k === 'name' || k === 'prototype') return;
      try {
        const d = Object.getOwnPropertyDescriptor(_origFetchRaw, k);
        if (d) Object.defineProperty(_wrappedFetch, k, d);
      } catch (e) { /* noop */ }
    });
  } catch (e) { /* noop */ }
  window.fetch = _wrappedFetch;
}
